// ============================================================
// PHOENIX OS — MOTOR DE MATRIZ & FILIAIS
// ============================================================
// Por que isso existe:
// No Brasil, o CNPJ tem uma estrutura que já diz quem é filial de quem:
//
//        11.222.333 / 0001 - 81
//        └────┬───┘   └─┬┘   └┬┘
//          RAIZ       ORDEM  DÍGITO
//        (8 dígitos)         VERIFICADOR
//
// - A RAIZ (8 primeiros dígitos) é a MESMA pra matriz e todas as filiais.
// - A ORDEM diz qual unidade é: 0001 = matriz, 0002+ = filiais.
//
// Ou seja: não precisamos pedir pro Consultor "linkar" filiais na mão nem
// criar um cadastro separado de vínculo. Dá pra detectar o grupo
// automaticamente a partir do CNPJ que ele já digita hoje. Menos trabalho
// pra ele, menos chance de erro, e funciona retroativamente pros clientes
// que já estão cadastrados.
// ============================================================

/** Tira tudo que não é número do CNPJ. */
export function limparCNPJ(cnpj) {
    return String(cnpj || '').replace(/\D/g, '');
}

/**
 * A RAIZ do CNPJ (8 primeiros dígitos) — o que matriz e filiais têm em comum.
 * Retorna null se não for um CNPJ utilizável (vazio, placeholder, incompleto).
 */
export function extrairRaizCNPJ(cnpj) {
    const nums = limparCNPJ(cnpj);
    if (nums.length !== 14) return null;
    // Ignora o placeholder padrão do sistema e CNPJs "zerados" — senão TODOS
    // os clientes sem CNPJ preenchido virariam "filiais uns dos outros".
    if (/^0+$/.test(nums)) return null;
    return nums.slice(0, 8);
}

/** O número de ordem da unidade (0001 = matriz, 0002+ = filial). */
export function extrairOrdemCNPJ(cnpj) {
    const nums = limparCNPJ(cnpj);
    if (nums.length !== 14) return null;
    return nums.slice(8, 12);
}

/** É matriz? (ordem 0001). CNPJ inválido/ausente conta como matriz por padrão. */
export function ehMatriz(cnpj) {
    const ordem = extrairOrdemCNPJ(cnpj);
    if (!ordem) return true;
    return ordem === '0001';
}

/** Formata só pra exibição: 11.222.333/0002-81 */
export function formatarCNPJ(cnpj) {
    const nums = limparCNPJ(cnpj);
    if (nums.length !== 14) return cnpj || '—';
    return `${nums.slice(0, 2)}.${nums.slice(2, 5)}.${nums.slice(5, 8)}/${nums.slice(8, 12)}-${nums.slice(12)}`;
}

/** Rótulo curto da unidade: "Matriz" ou "Filial 0002". */
export function rotuloUnidade(cnpj) {
    const ordem = extrairOrdemCNPJ(cnpj);
    if (!ordem) return 'Unidade';
    return ordem === '0001' ? 'Matriz' : `Filial ${ordem}`;
}

/**
 * Agrupa uma lista de clientes/empresas por raiz de CNPJ.
 *
 * Cada item da lista precisa ter pelo menos { id, name, cnpj }.
 *
 * Retorna uma lista de grupos:
 *   {
 *     raiz,            // '11222333' — ou null pros avulsos
 *     matriz,          // o cliente com ordem 0001 (pode ser null se só cadastraram filiais)
 *     filiais: [...],  // demais unidades, ordenadas
 *     unidades: [...], // matriz + filiais, tudo junto e ordenado
 *     ehGrupo          // true só quando tem 2+ unidades (senão é empresa comum)
 *   }
 *
 * Clientes sem CNPJ válido viram grupos individuais (ehGrupo: false) — nunca
 * somem da carteira, o que seria pior que não ter o agrupamento.
 */
export function agruparPorMatriz(clientes) {
    const porRaiz = new Map();
    const avulsos = [];

    (clientes || []).forEach((cliente) => {
        const raiz = extrairRaizCNPJ(cliente?.cnpj);
        if (!raiz) {
            avulsos.push(cliente);
            return;
        }
        if (!porRaiz.has(raiz)) porRaiz.set(raiz, []);
        porRaiz.get(raiz).push(cliente);
    });

    const grupos = [];

    porRaiz.forEach((unidades, raiz) => {
        const ordenadas = [...unidades].sort((a, b) =>
            String(extrairOrdemCNPJ(a.cnpj) || '9999').localeCompare(String(extrairOrdemCNPJ(b.cnpj) || '9999'))
        );
        const matriz = ordenadas.find(u => ehMatriz(u.cnpj)) || null;
        const filiais = ordenadas.filter(u => u !== matriz);
        grupos.push({
            raiz,
            matriz,
            filiais,
            unidades: ordenadas,
            ehGrupo: ordenadas.length > 1
        });
    });

    avulsos.forEach((cliente) => {
        grupos.push({ raiz: null, matriz: cliente, filiais: [], unidades: [cliente], ehGrupo: false });
    });

    return grupos;
}

/**
 * Dado um cliente e a carteira inteira, devolve as OUTRAS unidades do mesmo
 * grupo. Vazio quando o cliente não faz parte de nenhum grupo.
 */
export function irmasDoMesmoGrupo(cliente, todosClientes) {
    const raiz = extrairRaizCNPJ(cliente?.cnpj);
    if (!raiz) return [];
    return (todosClientes || []).filter(c =>
        c.id !== cliente.id && extrairRaizCNPJ(c.cnpj) === raiz
    );
}

/**
 * Consolida os números de várias unidades num único conjunto — é assim que
 * o Consultor enxerga "o grupo inteiro" em vez de unidade por unidade.
 * Soma o que é somável (receita, custo, dívida) e recalcula o que é
 * proporção (margem), pra nunca cair no erro de "média de porcentagens".
 */
export function consolidarGrupo(unidades, calcularMetricas) {
    const totais = { revenue: 0, costs: 0, debt: 0, ebitda: 0, equityValue: 0 };
    (unidades || []).forEach((u) => {
        const m = calcularMetricas ? calcularMetricas(u) : null;
        totais.revenue += Number(u.revenue) || 0;
        totais.costs += Number(u.costs) || 0;
        totais.debt += Number(u.debt) || 0;
        if (m) {
            totais.ebitda += Number(m.ebitdaAjustado ?? m.ebitda) || 0;
            totais.equityValue += Number(m.equityValue) || 0;
        }
    });
    // Margem consolidada do grupo = EBITDA total / receita total.
    // (Nunca a média das margens individuais: uma filial minúscula com margem
    // ótima distorceria o resultado do grupo inteiro.)
    totais.margemConsolidada = totais.revenue > 0 ? (totais.ebitda / totais.revenue) * 100 : 0;
    totais.alavancagemConsolidada = totais.ebitda > 0 ? totais.debt / (totais.ebitda * 12) : null;
    return totais;
}

/**
 * 🚨 Usado no perfil GESTOR: detecta quando alguém está cadastrando várias
 * unidades da MESMA empresa no perfil errado.
 *
 * O Gestor é feito pra analisar UMA empresa. Quem tem matriz + filiais e
 * precisa comparar unidades entre si é caso de Consultor. Sem esse aviso,
 * a pessoa cadastraria filiais soltas no Gestor, veria números que não
 * batem com a realidade do grupo e concluiria que o produto está errado.
 *
 * Retorna null quando está tudo certo, ou um objeto com o aviso.
 */
export function detectarFilialNoPerfilGestor(cnpjNovo, empresasExistentes) {
    const raizNova = extrairRaizCNPJ(cnpjNovo);
    if (!raizNova) return null;

    const mesmaRaiz = (empresasExistentes || []).filter(e => extrairRaizCNPJ(e.cnpj) === raizNova);
    if (mesmaRaiz.length === 0) return null;

    return {
        tipo: 'filial_no_gestor',
        raiz: raizNova,
        unidadeNova: rotuloUnidade(cnpjNovo),
        jaCadastradas: mesmaRaiz.map(e => ({ nome: e.name, unidade: rotuloUnidade(e.cnpj) })),
        titulo: 'Essa é outra unidade da mesma empresa',
        mensagem: 'O CNPJ que você digitou tem a mesma raiz de uma empresa que você já cadastrou — ou seja, é matriz e filial do mesmo grupo. O perfil Gestor é feito pra acompanhar uma empresa por vez, então cada unidade aqui é tratada de forma isolada, sem consolidar o grupo. Se você precisa comparar unidades e ver o resultado do grupo inteiro, o perfil Consultor é o indicado.'
    };
}